import { Avatar, Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { Panel } from '../../components/Panel';
import { ModeIcon } from '../shipments/ShipmentCard';
import { SHIPMENTS } from '../shipments/sample';
import { useShipments } from '../shipments/useShipments';
import { useCommunications } from './useCommunications';
import { PARTIES, type ActivityEvent, type ActivityStatus } from './sample';

/** "23 Jul 2025" → "23 Jul" */
const shortDate = (date: string) => date.split(' ').slice(0, 2).join(' ');

/** The events with one status: who, what, and when. The title carries the count. */
export function StatusList({ status, title, viewAll }: { status: ActivityStatus; title: string; viewAll?: string }) {
  const ships = useShipments().items;
  const events: ActivityEvent[] = useCommunications().events.filter((e) => e.status === status);
  return (
    <Panel title={title} count={events.length} viewAll={viewAll}>
      {events.length ? (
        <Stack spacing={1.25}>
          {events.map((e) => {
            const s = ships.find((x) => x.srb === e.srb) ?? SHIPMENTS.find((x) => x.srb === e.srb);
            return (
              <Stack
                key={e.id}
                component={RouterLink}
                to={`/communications/${e.srb}?party=${encodeURIComponent(e.party)}`}
                direction="row"
                spacing={1.25}
                sx={{ alignItems: 'center', color: 'inherit', textDecoration: 'none' }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  {s && <ModeIcon mode={s.mode} />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{e.srb}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {e.subject}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {shortDate(e.date)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <EmptyState>Nothing here.</EmptyState>
      )}
    </Panel>
  );
}

/** How many updates came from whom */
export function QuickStats() {
  const { events } = useCommunications();
  const stats = [
    { label: 'Total Updates', n: events.length },
    { label: 'Customers', n: events.filter((e) => e.party === PARTIES[0]).length },
    { label: 'Overseas Agents', n: events.filter((e) => e.party === PARTIES[1]).length },
    { label: 'Accountant', n: events.filter((e) => e.party === PARTIES[2]).length },
  ];
  return (
    <Panel title="Quick Stats" subtitle="This week">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
        {stats.map((s) => (
          <Stack key={s.label} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 750, fontSize: 22 }}>{s.n}</Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Panel>
  );
}
